"use client";

/**
 * useRealtimeMarketPrice - ZERO LAG Real-time Market Prices
 *
 * Architecture: Snapshot + Delta + Polling Fallback
 *
 * 1. SNAPSHOT: On mount, fetch fresh prices from CLOB via HTTP (immediate accuracy)
 * 2. DELTA: Subscribe to WebSocket for real-time updates
 * 3. FALLBACK: If WS fails, poll every 5 seconds to ensure freshness
 *
 * This guarantees:
 * - Users ALWAYS see fresh prices (no stale Gamma API data)
 * - WebSocket provides instant updates when available
 * - Automatic fallback to polling if WS fails
 * - Single source of truth for all price display
 */

import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { usePriceStream, PriceData } from "./usePriceStream";
import { useVisibilityRef } from "./useVisibilityRef";

// =============================================================================
// TYPES
// =============================================================================

export interface MarketLike {
  clobTokenIds?: string[];
  outcomePrices?: (number | string)[];
  outcomes?: string[];
  // Gamma API provides these more accurate prices
  lastTradePrice?: number;
  bestBid?: number;
  bestAsk?: number;
  /** Polymarket orderbook tick size — usually 0.01 (V1), or 0.001 for
   *  CLOB V2 low-probability markets. Used to round the initial Gamma
   *  price to a real on-book level so the button doesn't flicker from
   *  "52.5¢" → "53¢" when WS connects. */
  tickSize?: number;
}

/**
 * Snap a price to the nearest tick.
 *
 * If no tickSize is provided, returns the price unchanged rather than
 * snapping to 0.01 — V2 markets with tickSize=0.001/0.0001 were getting
 * rounded to 1¢ on grids, causing the displayed price to diverge from
 * the trading modal (which uses real tickSize from /clob-markets).
 *
 * Floating-point compensation: `Math.round(price/tick)*tick` can drift
 * (e.g. 0.99/0.01*0.01 = 0.9900000000000001). Round to the tick's own
 * decimal count to keep the displayed value clean.
 */
function snapToTick(price: number, tickSize?: number): number {
  if (!tickSize || tickSize <= 0) return price;
  const ticks = Math.round(price / tickSize);
  const decimals =
    tickSize >= 0.1 ? 1 :
    tickSize >= 0.01 ? 2 :
    tickSize >= 0.001 ? 3 : 4;
  return Number((ticks * tickSize).toFixed(decimals));
}

export interface UseRealtimeMarketPriceOptions {
  /** Array of markets to track prices for */
  markets: MarketLike[];
  /** Whether to enable price streaming (default: true) */
  enabled?: boolean;
}

export interface BuyPrices {
  /** Price to buy YES (bestAsk of YES token) */
  buyYes: number;
  /** Price to buy NO (1 - bestBid of YES token, or bestAsk of NO token) */
  buyNo: number;
  /** Whether these are real orderbook prices or approximations */
  isRealOrderbook: boolean;
}

export interface UseRealtimeMarketPriceReturn {
  /**
   * Get real-time price for a binary market (% CHANCE)
   * Uses lastTradePrice - what Polymarket shows as the percentage
   * @returns [yesPrice, noPrice] tuple (0-1 range)
   */
  getMarketPrice: (market: MarketLike) => [number, number];

  /**
   * Get real-time BUY prices for a binary market
   * Uses bestAsk/bestBid from orderbook - what Polymarket shows on Buy buttons
   * @returns { buyYes, buyNo, isRealOrderbook }
   */
  getBuyPrices: (market: MarketLike) => BuyPrices;

  /**
   * Get real-time price for a specific outcome index
   * @returns price (0-1 range)
   */
  getOutcomePrice: (market: MarketLike, outcomeIndex: number) => number;

  /**
   * Get raw price data from WebSocket
   */
  getRawPriceData: (assetId: string) => PriceData | undefined;

  /** Whether prices are from real-time source (WS or recent HTTP) */
  isRealtime: boolean;

  /** Whether WebSocket is connected */
  isConnected: boolean;

  /** Last update timestamp */
  lastUpdate: number;

  /** Connection error if any */
  error: string | null;
}

// =============================================================================
// CONSTANTS
// =============================================================================

// Polling intervals - dynamic based on WebSocket connection state.
//
// Polymarket's upstream WS has two failure modes:
//   1) Hard disconnect — Socket.IO sees `isConnected=false`, we
//      reconnect, no problem.
//   2) Silent freeze — connection stays open and ping/pong works, but
//      Polymarket stops emitting events for 5-20min (their known
//      issue, see backend price-stream.ts:44). On this path
//      `isConnected` lies — the buttons appeared frozen because
//      `SNAPSHOT_INTERVAL_WS_CONNECTED_MS=120000` waited 2 minutes
//      between safety-net polls.
//
// Bumped from 120s → 10s for the connected path. /prices/batch
// caches CLOB responses for 2s server-side, so the marginal cost of
// polling 12× more often is small. WS still beats it to the user
// when working — the polling just guarantees a 10s upper bound on
// staleness when WS goes silent.
const SNAPSHOT_INTERVAL_WS_CONNECTED_MS = 10000; // 10s — silent-freeze safety net
const SNAPSHOT_INTERVAL_WS_DISCONNECTED_MS = 15000; // 15s when WS fails (avoid rate limits)
const WS_CONNECT_TIMEOUT_MS = 3000; // Wait 3 seconds for WS before falling back
const PRICE_FRESHNESS_MS = 60000; // Consider price stale after 60 seconds
const MIN_FETCH_INTERVAL_MS = 5000; // Minimum 5s between any fetches (global debounce)
// Watchdog threshold — when isConnected=true but no WS price update
// has arrived in this window, treat the connection as silently
// frozen and force a Socket.IO reconnect. 60s is generous enough that
// liquid markets stay quiet for trivial reasons but not so generous
// that users sit on dead prices for minutes.
const WS_SILENT_FREEZE_THRESHOLD_MS = 60000;

// Max markets to use WebSocket for - above this threshold, only use HTTP polling
// This prevents rate limit issues (server allows 50 subscribes per 10s window)
// Small/medium lists (up to 50) use WS, large lists (100-400+) use polling only
const MAX_MARKETS_FOR_WEBSOCKET = 50;

// =============================================================================
// HTTP SNAPSHOT CACHE (shared across all hook instances)
// =============================================================================

interface SnapshotPrice {
  midPrice: number;
  lastTradePrice: number | null; // Actual last trade (for CHANCE) - null if no trades
  bestBid: number | null;
  bestAsk: number | null;
  timestamp: number;
}

const snapshotCache = new Map<string, SnapshotPrice>();
let snapshotFetchInProgress = false;
let lastSnapshotTime = 0;

// Last known prices cache (Gamma API fallback) - NEVER returns 0.5
// This stores prices from initial market data load to use as final fallback
const gammaFallbackCache = new Map<string, { yesPrice: number; noPrice: number }>();

// Max tokens per batch request (backend limit is 50)
const BATCH_SIZE = 50;

/**
 * Fetch fresh prices from CLOB via HTTP batch endpoint
 * Automatically splits large arrays into batches of 50
 */
async function fetchPriceSnapshot(tokenIds: string[]): Promise<void> {
  // Filter to only valid, non-empty strings before making any requests
  const validTokenIds = tokenIds.filter(id => typeof id === 'string' && id.trim().length > 0);
  if (validTokenIds.length === 0 || snapshotFetchInProgress) return;

  // Debounce: don't fetch if we just fetched (global across all hook instances)
  const now = Date.now();
  if (now - lastSnapshotTime < MIN_FETCH_INTERVAL_MS) return;

  snapshotFetchInProgress = true;
  lastSnapshotTime = now;

  try {
    // Split validTokenIds into batches of BATCH_SIZE
    const batches: string[][] = [];
    for (let i = 0; i < validTokenIds.length; i += BATCH_SIZE) {
      batches.push(validTokenIds.slice(i, i + BATCH_SIZE));
    }

    // Fetch batches SEQUENTIALLY to avoid rate limits (429 errors)
    // Only fetch the first 2 batches (100 markets max) to reduce load
    const maxBatches = Math.min(batches.length, 2);
    for (let i = 0; i < maxBatches; i++) {
      const batch = batches[i];
      try {
        const res = await fetch("/api/markets/prices/batch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenIds: batch }),
        });

        if (!res.ok) continue;

        const data = await res.json();
        if (!data?.prices) continue;

        const { prices, timestamp } = data as {
          prices: Record<string, {
            bestBid: number | null;
            bestAsk: number | null;
            midPrice: number | null;
            lastTradePrice?: number | null;
          }>;
          timestamp: number;
        };

        // Update snapshot cache
        for (const [tokenId, priceData] of Object.entries(prices)) {
          if (priceData.midPrice !== null) {
            snapshotCache.set(tokenId, {
              midPrice: priceData.midPrice,
              lastTradePrice: priceData.lastTradePrice ?? null,
              bestBid: priceData.bestBid,
              bestAsk: priceData.bestAsk,
              timestamp,
            });
          }
        }

        // Small delay between batches to avoid rate limits
        if (i < maxBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch {
        // Continue with next batch on error
      }
    }
  } catch {
    // Silently fail - we have fallback
  } finally {
    snapshotFetchInProgress = false;
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Extract asset IDs from an array of markets
 * Filters out empty strings, whitespace-only strings, and non-string values
 */
function extractAssetIds(markets: MarketLike[] | undefined | null): string[] {
  if (!markets || !Array.isArray(markets)) return [];
  const ids: string[] = [];
  const seen = new Set<string>();

  for (const market of markets) {
    const tokenIds = market.clobTokenIds;
    if (tokenIds && Array.isArray(tokenIds)) {
      // Subscribe to both YES (index 0) and NO (index 1) tokens
      // to get real bestAsk for each side
      for (const id of tokenIds) {
        if (typeof id === 'string' && id.trim().length > 0 && !seen.has(id)) {
          seen.add(id);
          ids.push(id);
        }
      }
    }
  }

  return ids;
}

/**
 * Parse a price value that could be number or string
 */
function parsePrice(value: number | string | undefined, fallback: number = 0.5): number {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "number") return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Normalize `outcomePrices` to an array regardless of upstream shape.
 * The backend `/api/markets` returns this field as a stringified JSON like
 * `'["0.9135","0.0865"]'`. SSR goes through `slim-market.ts` which parses
 * it; client-side fetches in `MarketsPageContent` skip that step, so a raw
 * string lands here and `outcomePrices?.[0]` returns `"["` — `parsePrice`
 * sees NaN and falls back to `0.5`, so binary markets paint as 50/50 until
 * WS hydrates. Coerce here so both code paths behave the same.
 */
function coerceOutcomePrices(value: unknown): (number | string)[] | null {
  const arr = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? (() => {
          try { const p = JSON.parse(value); return Array.isArray(p) ? p : null; } catch { return null; }
        })()
      : null;
  if (!arr) return null;
  // Validate every entry is something a downstream parsePrice can handle.
  // Reject non-numeric items so a malformed payload (`'[true,false]'`,
  // `'[null,null]'`) doesn't flow through and produce NaN totals in
  // TradingModal / EventCard.
  for (const v of arr) {
    if (typeof v === 'number') {
      if (!Number.isFinite(v)) return null;
    } else if (typeof v === 'string') {
      if (!Number.isFinite(Number(v))) return null;
    } else {
      return null;
    }
  }
  return arr;
}

// =============================================================================
// HOOK
// =============================================================================

export function useRealtimeMarketPrice(
  options: UseRealtimeMarketPriceOptions
): UseRealtimeMarketPriceReturn {
  const { markets, enabled = true } = options;

  // Extract all asset IDs from markets
  const assetIds = useMemo(() => extractAssetIds(markets), [markets]);
  const assetIdsKey = useMemo(() => assetIds.sort().join(","), [assetIds]);

  // State
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [wsReady, setWsReady] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const wsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const visRef = useVisibilityRef();
  // Skip snapshot/WS cache on first render to match SSR (which has no cache)
  // This prevents hydration mismatch when snapshotCache has stale client data
  const hasMountedRef = useRef(false);

  // Subscribe to WebSocket price stream
  // Only use WS for small number of markets (detail pages, events)
  // Lists with many markets use HTTP polling only (more efficient, avoids rate limits)
  const useWebSocket = assetIds.length <= MAX_MARKETS_FOR_WEBSOCKET;
  // `lastWsUpdateRef` tracks the timestamp of the most recent
  // `price:update` event delivered by the upstream Socket.IO. Distinct
  // from `lastUpdate` (which also fires on every snapshot poll), so
  // the silent-freeze watchdog below can tell the difference between
  // "WS is delivering" and "WS pretends to be connected but no events
  // are coming through".
  const lastWsUpdateRef = useRef(Date.now());
  const { getPrice: getWsPrice, isConnected, error, forceReconnect: forceWsReconnect } = usePriceStream({
    assetIds,
    enabled: enabled && useWebSocket && assetIds.length > 0,
    onPriceUpdate: () => {
      lastWsUpdateRef.current = Date.now();
      setLastUpdate(Date.now());
    },
  });

  // Reset the silent-freeze marker whenever the connection is freshly
  // (re)established, so a clean reconnect doesn't immediately trip
  // the 60s watchdog because of pre-disconnect quietness.
  useEffect(() => {
    if (isConnected) {
      lastWsUpdateRef.current = Date.now();
    }
  }, [isConnected]);

  // Silent-freeze watchdog. Polymarket's CLOB WS occasionally stops
  // emitting events while ping/pong stays healthy (their known bug,
  // see backend price-stream.ts:44). Without this watchdog the buttons
  // would sit on a stale price for up to 5 minutes (the backend's
  // own DATA_TIMEOUT_MS) — by detecting the silence client-side and
  // forcing a Socket.IO reconnect we tell the backend to drop the
  // dead upstream and re-establish, all in one tab. Only fires when
  // the socket claims to be connected.
  useEffect(() => {
    if (!enabled || !useWebSocket || assetIds.length === 0) return;
    if (!isConnected) return;

    const id = setInterval(() => {
      if (!visRef.current) return; // tab in background, don't bother
      const silentMs = Date.now() - lastWsUpdateRef.current;
      if (silentMs > WS_SILENT_FREEZE_THRESHOLD_MS) {
        // Reset the marker before reconnect so we don't fire repeatedly
        // while the new socket comes up.
        lastWsUpdateRef.current = Date.now();
        forceWsReconnect();
      }
    }, 15_000);

    return () => clearInterval(id);
  }, [enabled, useWebSocket, assetIds.length, isConnected, forceWsReconnect, visRef]);

  // Track WS connection state and adjust polling interval dynamically
  useEffect(() => {
    if (isConnected) {
      setWsReady(true);
      // Clear WS timeout
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
        wsTimeoutRef.current = null;
      }
    }
  }, [isConnected]);

  // Dynamic polling: fast when WS disconnected, slow when connected
  // This ensures we always have prices but don't waste bandwidth when WS works
  useEffect(() => {
    if (!enabled || assetIds.length === 0) return;

    mountedRef.current = true;

    // Fetch initial snapshot immediately
    fetchPriceSnapshot(assetIds).then(() => {
      if (mountedRef.current) {
        hasMountedRef.current = true;
        setLastUpdate(Date.now());
      }
    });

    // Clear existing polling interval
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    // Set polling interval based on WS connection state
    // When WS connected: 30s (safety net only)
    // When WS disconnected: 2s (active fallback)
    const interval = isConnected
      ? SNAPSHOT_INTERVAL_WS_CONNECTED_MS
      : SNAPSHOT_INTERVAL_WS_DISCONNECTED_MS;

    pollingRef.current = setInterval(() => {
      if (mountedRef.current && assetIds.length > 0 && visRef.current) {
        fetchPriceSnapshot(assetIds).then(() => {
          if (mountedRef.current) {
            setLastUpdate(Date.now());
          }
        });
      }
    }, interval);

    return () => {
      mountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
        wsTimeoutRef.current = null;
      }
    };
  }, [enabled, assetIdsKey, isConnected]); // Re-run when WS connection changes

  /**
   * Get real-time price for a binary market
   * Priority: WS/Snapshot (if not suspicious) > Gamma API
   *
   * IMPORTANT: When CLOB has huge spread (bid ~0, ask ~1), midPrice is ~0.5
   * which is meaningless. In these cases, use Gamma prices instead.
   */
  const getMarketPrice = useCallback(
    (market: MarketLike): [number, number] => {
      const tokenId = market.clobTokenIds?.[0];

      // Get Gamma API lastTradePrice first (most accurate from API)
      // Priority: market.lastTradePrice > mid(bestBid,bestAsk) > outcomePrices
      let gammaYes: number;
      let gammaNo: number;

      if (market.lastTradePrice && market.lastTradePrice > 0) {
        // Best: Gamma API lastTradePrice (what Polymarket UI shows)
        gammaYes = market.lastTradePrice;
        gammaNo = 1 - gammaYes;
      } else if (market.bestAsk && market.bestAsk > 0 && market.bestAsk < 1) {
        // Second best: ask price (what you'd pay to buy — matches trading panel)
        gammaYes = market.bestAsk;
        gammaNo = 1 - gammaYes;
      } else if (market.bestBid && market.bestAsk && market.bestBid > 0 && market.bestAsk > 0) {
        // Third: mid-price from orderbook
        gammaYes = (market.bestBid + market.bestAsk) / 2;
        gammaNo = 1 - gammaYes;
      } else {
        // Fallback: outcomePrices (less accurate). Coerce because client-side
        // fetches receive this as a stringified JSON array.
        const op = coerceOutcomePrices(market.outcomePrices);
        gammaYes = parsePrice(op?.[0]);
        gammaNo = parsePrice(op?.[1], 1 - gammaYes);
      }

      // Helper to check if CLOB price is suspicious (near 0.5 = huge spread)
      const isSuspiciousPrice = (price: number): boolean => {
        // If CLOB says ~50% but Gamma says something significantly different, trust Gamma
        // This catches markets with empty orderbooks where midPrice defaults to 50%
        const isNear50 = price > 0.45 && price < 0.55;
        // Consider suspicious if Gamma differs from 50% by more than 15%
        // This catches cases like Gamma=68% which is 18% away from 50%
        const gammaFarFrom50 = Math.abs(gammaYes - 0.5) > 0.15;
        return isNear50 && gammaFarFrom50;
      };

      if (tokenId && hasMountedRef.current) {
        // Get sources (skip on first render to match SSR — no cache on server)
        const wsData = getWsPrice(tokenId);
        const snapshot = snapshotCache.get(tokenId);

        // 1. WS lastTradePrice is the most authoritative source (real trade happened)
        if (wsData?.lastTradePrice != null && !isSuspiciousPrice(wsData.lastTradePrice)) {
          gammaFallbackCache.set(tokenId, { yesPrice: wsData.lastTradePrice, noPrice: 1 - wsData.lastTradePrice });
          return [wsData.lastTradePrice, 1 - wsData.lastTradePrice];
        }

        // 2. HTTP snapshot lastTradePrice (actual last trade for CHANCE)
        if (snapshot?.lastTradePrice !== null && snapshot?.lastTradePrice !== undefined && !isSuspiciousPrice(snapshot.lastTradePrice)) {
          gammaFallbackCache.set(tokenId, { yesPrice: snapshot.lastTradePrice, noPrice: 1 - snapshot.lastTradePrice });
          return [snapshot.lastTradePrice, 1 - snapshot.lastTradePrice];
        }

        // 3. WS bestAsk (what you'd pay to buy — matches trading panel)
        // Preferred over midPrice for markets without trades (shows actionable price)
        if (wsData?.bestAsk != null && wsData.bestAsk > 0 && wsData.bestAsk < 1 && !isSuspiciousPrice(wsData.bestAsk)) {
          gammaFallbackCache.set(tokenId, { yesPrice: wsData.bestAsk, noPrice: 1 - wsData.bestAsk });
          return [wsData.bestAsk, 1 - wsData.bestAsk];
        }

        // 4. HTTP snapshot midPrice (could be orderbook mid if no last trade)
        if (snapshot && !isSuspiciousPrice(snapshot.midPrice)) {
          gammaFallbackCache.set(tokenId, { yesPrice: snapshot.midPrice, noPrice: 1 - snapshot.midPrice });
          return [snapshot.midPrice, 1 - snapshot.midPrice];
        }

        // 5. WS midPrice is fallback (only if not suspicious)
        if (wsData?.midPrice != null && !isSuspiciousPrice(wsData.midPrice)) {
          gammaFallbackCache.set(tokenId, { yesPrice: wsData.midPrice, noPrice: 1 - wsData.midPrice });
          return [wsData.midPrice, 1 - wsData.midPrice];
        }

        // 6. Try gamma fallback cache (last known good price)
        const gammaCache = gammaFallbackCache.get(tokenId);
        if (gammaCache && gammaCache.yesPrice !== 0.5) {
          return [gammaCache.yesPrice, gammaCache.noPrice];
        }
      }

      // 4. Use Gamma prices (always the fallback)
      if (tokenId && gammaYes !== 0.5) {
        gammaFallbackCache.set(tokenId, { yesPrice: gammaYes, noPrice: gammaNo });
      }

      return [gammaYes, gammaNo];
    },
    [getWsPrice]
  );

  /**
   * Get real-time price for a specific outcome index
   */
  const getOutcomePrice = useCallback(
    (market: MarketLike, outcomeIndex: number): number => {
      if (outcomeIndex < 2) {
        const [yesPrice, noPrice] = getMarketPrice(market);
        return outcomeIndex === 0 ? yesPrice : noPrice;
      }
      const op = coerceOutcomePrices(market.outcomePrices);
      return parsePrice(op?.[outcomeIndex], 0);
    },
    [getMarketPrice]
  );

  /**
   * Get real-time BUY prices for a binary market
   * Returns bestAsk for Buy Yes, and (1 - bestBid) for Buy No
   * This matches what Polymarket shows on their Buy buttons
   * Priority for each price independently: WS > Snapshot > fallback
   */
  const getBuyPrices = useCallback(
    (market: MarketLike): BuyPrices => {
      const yesTokenId = market.clobTokenIds?.[0];
      const noTokenId = market.clobTokenIds?.[1];

      // Default: use Gamma API bestAsk/bestBid if available (most accurate initial data),
      // otherwise fall back to lastTradePrice-based approximation.
      // Snap to the market's tick size (0.01 default, 0.001 on V2 fine-tick
      // markets) so the initial paint already matches what the WS update
      // will land on — avoids "52.5¢ → 53¢" flicker on hydrate.
      const [yesPrice] = getMarketPrice(market);
      let buyYes = (market.bestAsk != null && market.bestAsk > 0 && market.bestAsk < 1)
        ? snapToTick(market.bestAsk, market.tickSize)
        : snapToTick(yesPrice, market.tickSize);
      let buyNo = (market.bestBid != null && market.bestBid > 0 && market.bestBid < 1)
        ? snapToTick(1 - market.bestBid, market.tickSize)
        : snapToTick(1 - yesPrice, market.tickSize);
      let hasRealBuyYes = market.bestAsk != null && market.bestAsk > 0 && market.bestAsk < 1;
      let hasRealBuyNo = market.bestBid != null && market.bestBid > 0 && market.bestBid < 1;

      // YES token: bestAsk = price to buy YES
      if (yesTokenId) {
        const wsYes = getWsPrice(yesTokenId);
        const snapYes = snapshotCache.get(yesTokenId);

        if (wsYes?.bestAsk != null && wsYes.bestAsk > 0 && wsYes.bestAsk < 1) {
          buyYes = wsYes.bestAsk;
          hasRealBuyYes = true;
        } else if (snapYes?.bestAsk != null && snapYes.bestAsk > 0 && snapYes.bestAsk < 1) {
          buyYes = snapYes.bestAsk;
          hasRealBuyYes = true;
        }
      }

      // NO token: bestAsk = price to buy NO (real, not 1 - bestBid)
      // Falls back to 1 - bestBid(YES) if NO token data unavailable
      if (noTokenId) {
        const wsNo = getWsPrice(noTokenId);
        const snapNo = snapshotCache.get(noTokenId);

        if (wsNo?.bestAsk != null && wsNo.bestAsk > 0 && wsNo.bestAsk < 1) {
          buyNo = wsNo.bestAsk;
          hasRealBuyNo = true;
        } else if (snapNo?.bestAsk != null && snapNo.bestAsk > 0 && snapNo.bestAsk < 1) {
          buyNo = snapNo.bestAsk;
          hasRealBuyNo = true;
        }
      }
      // Fallback: 1 - bestBid(YES) if no NO token data
      if (!hasRealBuyNo && yesTokenId) {
        const wsYes = getWsPrice(yesTokenId);
        const snapYes = snapshotCache.get(yesTokenId);
        if (wsYes?.bestBid != null && wsYes.bestBid > 0 && wsYes.bestBid < 1) {
          buyNo = 1 - wsYes.bestBid;
          hasRealBuyNo = true;
        } else if (snapYes?.bestBid != null && snapYes.bestBid > 0 && snapYes.bestBid < 1) {
          buyNo = 1 - snapYes.bestBid;
          hasRealBuyNo = true;
        }
      }

      // Sanity check — in long-shot markets (e.g. Celtics NBA champion) the
      // raw CLOB book has MM-juiced asks at ~0.999 on the illiquid side, so
      // WS/snapshot bestAsk overrides the healthy Gamma price with junk and
      // the sum of asks exceeds 100% (impossible as a probability).
      // Polymarket's event grid never shows the raw book ask for this reason
      // — it uses Gamma's pre-filtered bestAsk/bestBid, and falls back to
      // lastTradePrice when even those are broken.
      const sum = buyYes + buyNo;
      const implausible = sum > 1.10 || buyYes >= 0.98 || buyNo >= 0.98;
      if (implausible) {
        // Tier 1: Gamma's filtered bestAsk/bestBid (what Polymarket pints)
        const gammaAsk = market.bestAsk;
        const gammaBid = market.bestBid;
        if (gammaAsk != null && gammaAsk > 0 && gammaAsk < 0.98) {
          buyYes = gammaAsk;
          hasRealBuyYes = false;
        }
        if (gammaBid != null && gammaBid > 0.02 && gammaBid < 1) {
          buyNo = 1 - gammaBid;
          hasRealBuyNo = false;
        }
        // Tier 2: still broken → lastTradePrice-based fair price
        if (buyYes + buyNo > 1.10 || buyYes >= 0.98 || buyNo >= 0.98) {
          const [fairYes, fairNo] = getMarketPrice(market);
          if (fairYes > 0 && fairYes < 1 && fairNo > 0 && fairNo < 1) {
            buyYes = fairYes;
            buyNo = fairNo;
            hasRealBuyYes = false;
            hasRealBuyNo = false;
          }
        }
      }

      return { buyYes, buyNo, isRealOrderbook: hasRealBuyYes || hasRealBuyNo };
    },
    [getMarketPrice, getWsPrice]
  );

  /**
   * Get raw price data from WebSocket
   */
  const getRawPriceData = useCallback(
    (assetId: string): PriceData | undefined => {
      return getWsPrice(assetId);
    },
    [getWsPrice]
  );

  // Determine if prices are real-time
  const isRealtime = useMemo(() => {
    // Real-time if WS is connected or snapshot is fresh
    if (isConnected) return true;

    // Check if any snapshot is fresh
    for (const tokenId of assetIds) {
      const snapshot = snapshotCache.get(tokenId);
      if (snapshot && Date.now() - snapshot.timestamp < PRICE_FRESHNESS_MS) {
        return true;
      }
    }

    return false;
    // snapshotCache is intentionally excluded - it's a module-level cache (not React state)
    // that persists across renders. Including it would cause infinite re-renders.
    // lastUpdate is used to trigger recalculation when new data arrives.
  }, [isConnected, assetIds, lastUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    getMarketPrice,
    getBuyPrices,
    getOutcomePrice,
    getRawPriceData,
    isRealtime,
    isConnected,
    lastUpdate,
    error,
  };
}

// =============================================================================
// CONVENIENCE WRAPPER FOR SINGLE MARKET
// =============================================================================

/**
 * Hook for tracking a single market's price
 * Simpler API when you only need one market
 */
export function useSingleMarketPrice(
  market: MarketLike | null | undefined,
  enabled: boolean = true
): {
  yesPrice: number;
  noPrice: number;
  isRealtime: boolean;
  isConnected: boolean;
} {
  const markets = useMemo(() => (market ? [market] : []), [market]);

  const { getMarketPrice, isRealtime, isConnected } = useRealtimeMarketPrice({
    markets,
    enabled: enabled && !!market,
  });

  const [yesPrice, noPrice] = market ? getMarketPrice(market) : [0.5, 0.5];

  return { yesPrice, noPrice, isRealtime, isConnected };
}

// =============================================================================
// UTILITY FUNCTIONS (for use outside React)
// =============================================================================

/**
 * Format price as percentage string with decimals
 * @example formatPricePercent(0.456) => "45.6"
 */
export function formatPricePercent(price: number, decimals: number = 1): string {
  return (price * 100).toFixed(decimals);
}

/**
 * Format price as cents string
 * @example formatPriceCents(0.456) => "45.6¢"
 */
export function formatPriceCents(price: number, decimals: number = 1): string {
  return `${(price * 100).toFixed(decimals)}¢`;
}

export default useRealtimeMarketPrice;
